import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import { isInternationalDomain } from '../config/domainLanguages';

export default function BetaOfferPopup() {
    const { t } = useTranslation('betaOffer');
    const [offer, setOffer] = useState(null);
    const [show, setShow] = useState(false);

    useEffect(() => {
        if (!isInternationalDomain()) return undefined;

        const token = localStorage.getItem('token');
        const role = localStorage.getItem('role');
        if (!token || role !== 'Owner') return undefined;

        const dismissed = sessionStorage.getItem('beta_offer_dismissed');
        if (dismissed) return undefined;

        let timer;
        axios.get(`${getApiBaseUrl()}/beta-offer/status`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => {
                if (res.data?.shouldShow) {
                    setOffer(res.data);
                    timer = setTimeout(() => setShow(true), 3000);
                }
            })
            .catch(() => {});

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, []);

    const handleDismiss = () => {
        setShow(false);
        sessionStorage.setItem('beta_offer_dismissed', 'true');
    };

    const handleAccept = async (type) => {
        const token = localStorage.getItem('token');
        try {
            await axios.post(`${getApiBaseUrl()}/beta-offer/accept`, null, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch {
            // checkout still opens even if accept fails
        }

        const url = type === 'monthly'
            ? offer.monthlyCheckoutUrl
            : offer.annualCheckoutUrl;
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
        setShow(false);
    };

    if (!show || !offer) return null;

    const daysLeft = Math.max(1, Math.ceil(
        (new Date(offer.offerExpiresAt) - new Date()) / (1000 * 60 * 60 * 24)
    ));

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.65)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
        }}>
            <div style={{
                background: '#fff',
                borderRadius: 20,
                maxWidth: 520,
                width: '100%',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #EB3333, #c0392b)',
                    padding: '32px 28px',
                    textAlign: 'center',
                    position: 'relative',
                }}>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        aria-label={t('dismiss')}
                        style={{
                            position: 'absolute', top: 12, right: 12,
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none', borderRadius: '50%',
                            width: 28, height: 28, cursor: 'pointer',
                            color: '#fff', fontSize: 16,
                        }}
                    >
                        ×
                    </button>
                    <div style={{ fontSize: 48, marginBottom: 8 }}>🏆</div>
                    <h2 style={{ color: '#fff', margin: 0, fontSize: 22 }}>
                        {t('title')}
                    </h2>
                    <p style={{
                        color: 'rgba(255,255,255,0.85)',
                        margin: '8px 0 0', fontSize: 14,
                    }}>
                        {t('subtitle')}
                    </p>
                </div>

                <div style={{ padding: 28 }}>
                    <div style={{
                        background: '#FFF8E1',
                        borderRadius: 10, padding: '12px 16px',
                        marginBottom: 20, fontSize: 14,
                        color: '#795548',
                    }}>
                        🏅 {t('founderBadge')}
                    </div>

                    <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                        <div style={{
                            flex: '1 1 200px', border: '2px solid #EB3333',
                            borderRadius: 12, padding: 16,
                            textAlign: 'center',
                        }}>
                            <div style={{ fontSize: 24, marginBottom: 4 }}>📅</div>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                                {t('monthly.title')}
                            </div>
                            <div style={{ color: '#EB3333', fontWeight: 700, fontSize: 20, marginBottom: 4 }}>
                                −50%
                            </div>
                            <div style={{ color: '#888', fontSize: 12 }}>
                                {t('monthly.desc')}
                            </div>
                            <button
                                type="button"
                                onClick={() => handleAccept('monthly')}
                                style={{
                                    marginTop: 12, width: '100%',
                                    background: '#EB3333', color: '#fff',
                                    border: 'none', borderRadius: 8,
                                    padding: '10px', fontWeight: 700,
                                    cursor: 'pointer', fontSize: 13,
                                }}
                            >
                                {t('monthly.cta')}
                            </button>
                        </div>

                        <div style={{
                            flex: '1 1 200px',
                            border: '2px solid #2c3e50',
                            borderRadius: 12, padding: 16,
                            textAlign: 'center',
                            position: 'relative',
                        }}>
                            <div style={{
                                position: 'absolute', top: -10,
                                left: '50%', transform: 'translateX(-50%)',
                                background: '#2c3e50', color: '#fff',
                                fontSize: 11, fontWeight: 700,
                                padding: '2px 10px', borderRadius: 20,
                            }}>
                                {t('annual.badge')}
                            </div>
                            <div style={{ fontSize: 24, marginBottom: 4 }}>💎</div>
                            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                                {t('annual.title')}
                            </div>
                            <div style={{ color: '#2c3e50', fontWeight: 700, fontSize: 20, marginBottom: 4 }}>
                                −50%
                            </div>
                            <div style={{ color: '#888', fontSize: 12 }}>
                                {t('annual.desc')}
                            </div>
                            <button
                                type="button"
                                onClick={() => handleAccept('annual')}
                                style={{
                                    marginTop: 12, width: '100%',
                                    background: '#2c3e50', color: '#fff',
                                    border: 'none', borderRadius: 8,
                                    padding: '10px', fontWeight: 700,
                                    cursor: 'pointer', fontSize: 13,
                                }}
                            >
                                {t('annual.cta')}
                            </button>
                        </div>
                    </div>

                    <div style={{
                        textAlign: 'center', color: '#888',
                        fontSize: 13, borderTop: '1px solid #eee',
                        paddingTop: 16,
                    }}>
                        ⏳ {t('expiresIn', { days: daysLeft })}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: 12 }}>
                        <button
                            type="button"
                            onClick={handleDismiss}
                            style={{
                                background: 'none', border: 'none',
                                color: '#bbb', cursor: 'pointer',
                                fontSize: 12, textDecoration: 'underline',
                            }}
                        >
                            {t('dismiss')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
